
import { Button } from "@/components/ui/button";
import { useRef, useState } from "react";
import { BulkUploadGuards } from "./BulkUploadGuards";
import { AddGuardForm } from "./AddGuardForm";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { PlusCircle, Upload, UserPlus } from "lucide-react";
import { ArchivedGuardsDialog } from "./ArchivedGuardsDialog";
import { useUserRoles } from "@/hooks/useUserRoles";
import { Guard, Team } from "../types";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLanguage } from "@/contexts/LanguageContext";

export interface GuardsHeaderProps {
  onGuardAdded?: () => void;
  teams?: Team[];
  guards?: Guard[];
  isLoading?: boolean;
  onGuardRemoved?: () => void;
  onGuardSelect?: (guard: Guard) => void;
}

export const GuardsHeader = ({ onGuardAdded, teams }: GuardsHeaderProps) => {
  const { t } = useLanguage();
  const [showAddGuardDialog, setShowAddGuardDialog] = useState(false);
  const [showBulkUploadDialog, setShowBulkUploadDialog] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const { isAdmin } = useUserRoles();
  const isMobile = useIsMobile();

  const handleAddGuard = () => {
    if (onGuardAdded) {
      onGuardAdded();
    }
    setShowAddGuardDialog(false);
  };

  const handleBulkUpload = () => {
    if (onGuardAdded) {
      onGuardAdded();
    }
    setShowBulkUploadDialog(false);
  };

  return (
    <div className="relative">
      {/* Hero section with enhanced styling */}
      <div className="bg-gradient-to-r from-card/80 via-card/60 to-card/40 backdrop-blur-sm rounded-2xl border border-border/50 shadow-lg hover:shadow-xl transition-all duration-300 p-6 mb-8">
        <div className={`${isMobile ? 'space-y-6' : 'flex justify-between items-center'}`}>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center">
                <UserPlus className="w-5 h-5 text-white" />
              </div>
              <div>
        <h1 className="text-3xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
          Personnel
        </h1>
                <p className="text-muted-foreground text-sm mt-1">
                  {t('manageSecurityTeam')}
                </p>
              </div>
            </div>
          </div>
          
          {isAdmin && (
            <div className={`flex ${isMobile ? 'flex-col space-y-3 w-full mt-4' : 'gap-3'}`}>
              <ArchivedGuardsDialog onGuardRestored={onGuardAdded || (() => {})} />
              
              <Dialog open={showAddGuardDialog} onOpenChange={setShowAddGuardDialog}>
                <DialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    className={`gap-2 hover-scale border-primary/20 hover:border-primary/40 hover:bg-primary/5 ${isMobile ? 'w-full justify-start h-12' : 'h-11'}`}
                  >
                    <UserPlus className="w-4 h-4" />
                    Add Personnel
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[700px]">
                  <DialogHeader>
                    <DialogTitle>Add New Personnel</DialogTitle>
                    <DialogDescription>
                      Create a new personnel account. Required fields are marked with *.
                    </DialogDescription>
                  </DialogHeader>
                  <AddGuardForm 
                    teams={teams}
                    onSuccess={handleAddGuard} 
                    onClose={() => setShowAddGuardDialog(false)}
                    formRef={formRef} 
                  />
                </DialogContent>
              </Dialog>

              <Dialog open={showBulkUploadDialog} onOpenChange={setShowBulkUploadDialog}>
                <DialogTrigger asChild>
                  <Button 
                    className={`gap-2 hover-scale bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 shadow-md hover:shadow-lg ${isMobile ? 'w-full justify-start h-12' : 'h-11'}`}
                  >
                    <Upload className="w-4 h-4" />
                    {t('bulkUpload')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{t('bulkUploadGuards')}</DialogTitle>
                    <DialogDescription>
                      {t('uploadMultipleGuards')}
                    </DialogDescription>
                  </DialogHeader>
                  <BulkUploadGuards 
                    onSuccess={handleBulkUpload}
                    onClose={() => setShowBulkUploadDialog(false)}
                  />
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
